import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import styles from './VisionMission.module.css';

export default function VisionMission() {
  const [isFullOpen, setIsFullOpen] = useState(false);
  const headlineRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    let isVisible = false;

    // Only listen when the headline is inside or near the viewport
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
      },
      { rootMargin: '120px 0px 120px 0px' }
    );

    if (headlineRef.current) {
      observer.observe(headlineRef.current);
    }

    const handleScroll = () => {
      if (!isVisible || !headlineRef.current) return;

      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;
      lastScrollY.current = currentY;

      // Filter out micro-jitter
      if (Math.abs(diff) < 2.5) return;

      const isDown = diff > 0;
      const addClass = isDown ? styles.glitchDown : styles.glitchUp;
      const removeClass = isDown ? styles.glitchUp : styles.glitchDown;

      const words = headlineRef.current.querySelectorAll(`.${styles.glitchWord}`);
      words.forEach(word => {
        word.classList.remove(removeClass);
        word.classList.add(addClass);
      });

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Smoothly melt back to normal crisp text once scrolling stops
      scrollTimeoutRef.current = setTimeout(() => {
        if (!headlineRef.current) return;
        const activeWords = headlineRef.current.querySelectorAll(`.${styles.glitchWord}`);
        activeWords.forEach(word => {
          word.classList.remove(styles.glitchDown, styles.glitchUp);
        });
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, []);

  return (
    <section className={styles.section} id="vision-mission">
      <div className={styles.container}>
        {/* ── Above Vision & Mission: Manifesto Lead Header ── */}
        <div className={styles.manifestoLeadWrap}>
          <h2 ref={headlineRef} className={styles.manifestoHeadline}>
            BORN IN <em className={styles.glitchWord}>REVIVAL</em>. BUILT ON THE <em className={styles.glitchWord}>WORD</em>.<br />
            CARRYING THE <em className={styles.glitchWord}>FIRE</em> TO THE NATIONS.
          </h2>
          <div className={styles.manifestoIntro}>
            <p className={styles.introHighlight}>
              Faith Fellowship Church is a church born out of revival, built through faith, and entrusted with a mandate for generations.
            </p>
            <p className={styles.introSub}>
              Since <strong>18 November 1998</strong>, God has been writing a story of revival, transformation, and mission through this house. What began as a move of the Holy Spirit became a church. And what became a church has become a movement that continues to carry the fire forward.
            </p>
          </div>
        </div>

        {/* ── Professional Unified Action Hub (Story Toggle) ── */}
        <div className={styles.actionHub}>
          <button
            type="button"
            className={`${styles.storyToggleBtn} ${isFullOpen ? styles.storyToggleActive : ''}`}
            onClick={() => setIsFullOpen(prev => !prev)}
            aria-expanded={isFullOpen}
          >
            <span className={styles.iconFlame} aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 3z" />
              </svg>
            </span>
            <span className={styles.btnLabel}>
              {isFullOpen ? 'Collapse Revival Story' : 'Read Full Revival Story & Manifesto'}
            </span>
            <span className={`${styles.chevronIcon} ${isFullOpen ? styles.chevronOpen : ''}`} aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          </button>
        </div>

        {/* ── Expandable Full Area ("Rest all put read full area") ── */}
        {isFullOpen && (
          <div className={styles.fullManifestoContent}>
            {/* Section 1 */}
            <div className={styles.storySection}>
              <span className={styles.storyEyebrow}>01 &bull; OUR ORIGINS</span>
              <h4 className={styles.storyHeading}>ONE FIRE. MANY GENERATIONS. ONE MISSION.</h4>
              <p className={styles.storyLeadQuote}>
                “There are things God starts in one generation that He intends to continue in another.”
              </p>
              <p className={styles.storyPara}>
                In 1998, <strong>Pr. Titus Chacko</strong>, <strong>Pr. Koshy Mathai</strong>, and <strong>Pr. Varkey George</strong> stood together in a season of revival. Through their ministry, lives were touched, hearts were awakened, and a spiritual fire began to burn.
              </p>
              <p className={styles.storyPara}>
                That fire became the foundation of Faith Fellowship Church. But the story did not end with the pioneers. The fire was never meant to stop with one generation.
              </p>
            </div>

            <div className={styles.storyDivider}>⸻</div>

            {/* Section 2 */}
            <div className={styles.storySection}>
              <span className={styles.storyEyebrow}>02 &bull; THE SUCCESSION</span>
              <h4 className={styles.storyHeading}>THE BATON HAS BEEN PASSED. AND THE FIRE IS MOVING FORWARD.</h4>
              <p className={styles.storyPara}>
                Today, <strong>Pr. Joby Titus</strong> carries the baton as Senior Pastor, continuing the spiritual heritage and revival mandate entrusted to the next generation.
              </p>
              <p className={styles.storyPara}>
                The assignment is not simply to preserve yesterday’s fire. It is to carry it further:
              </p>
              <ul className={styles.storyList}>
                <li>To raise a generation that knows God.</li>
                <li>To build a church that walks in the power of the Holy Spirit.</li>
                <li>To raise a remnant that will stand for Christ.</li>
                <li>And to send that remnant beyond the walls of the church and into the nations.</li>
              </ul>
            </div>

            <div className={styles.storyDivider}>⸻</div>

            {/* Section 3 */}
            <div className={styles.storySection}>
              <span className={styles.storyEyebrow}>03 &bull; GLOBAL IMPACT</span>
              <h4 className={styles.storyHeading}>FROM A LOCAL REVIVAL TO A GLOBAL MANDATE.</h4>
              <p className={styles.storyPara}>
                What God began in one place has continued to expand. Faith Fellowship Church is now extending its ministry beyond its original boundaries, reaching into India and mission fields across Africa.
              </p>
              <blockquote className={styles.storyBlockquote}>
                “Because we believe the Gospel was never given to be contained. We are called to go. From our cities to our nations. From our communities to the continents. From one generation to the next. Until the nations hear.”
              </blockquote>
            </div>

            <div className={styles.storyDivider}>⸻</div>

            {/* Section 4 */}
            <div className={styles.storySection}>
              <span className={styles.storyEyebrow}>04 &bull; OUR PASSION</span>
              <h4 className={styles.storyHeading}>WE ARE RAISING THE REMNANT.</h4>
              <p className={styles.storyPara}>
                The world does not simply need another church. It needs a people who carry the presence of God. Our passion is to see men and women encounter Jesus, become rooted in His Word, empowered by His Spirit, transformed in their identity, and equipped for their assignment.
              </p>
              <div className={styles.remnantCallout}>
                <p className={styles.calloutIntro}>
                  We believe there is a generation rising. A generation that will not compromise. A generation that will not be silent. A generation that will carry the fire.
                </p>
                <div className={styles.remnantPillars}>
                  <div className={styles.remnantBadge}>✦ A REMNANT THAT WILL RISE</div>
                  <div className={styles.remnantBadge}>✦ A REMNANT THAT WILL BURN</div>
                  <div className={styles.remnantBadge}>✦ A REMNANT THAT WILL BE SENT</div>
                </div>
              </div>
            </div>

            <div className={styles.storyDivider}>⸻</div>

            {/* Section 5 */}
            <div className={styles.storySection}>
              <span className={styles.storyEyebrow}>05 &bull; THE MOVEMENT</span>
              <h4 className={styles.storyHeading}>THIS IS MORE THAN A CHURCH. THIS IS A MOVEMENT OF GOD.</h4>
              <ul className={styles.movementList}>
                <li>A movement that began in revival.</li>
                <li>A movement that crossed generations.</li>
                <li>A movement that is crossing borders.</li>
                <li>A movement that believes the best is still ahead.</li>
              </ul>
              <p className={styles.storyPara}>
                The same God who started the fire is still faithful to carry it forward. The pioneers carried the fire. The next generation received the baton. Now we carry it to the nations.
              </p>
            </div>

            <div className={styles.storyDivider}>⸻</div>

            {/* Section 6: Final Grand Anthem */}
            <div className={styles.finalAnthemCard}>
              <span className={styles.finalTimelineBadge}>1998 &rarr; TODAY &rarr; THE NATIONS</span>
              <h4 className={styles.finalAnthemTitle}>THE FIRE IS STILL BURNING.</h4>
              <p className={styles.finalAnthemSubtitle}>
                One story. One faith. One Gospel. One mission.<br />
                One generation handing the fire to another until Jesus comes.
              </p>
              <div className={styles.churchCredo}>
                <strong>FAITH FELLOWSHIP CHURCH</strong>
                <span>Born in Revival &bull; Built for Generations &bull; Sent to the Nations</span>
              </div>
            </div>

            <div className={styles.closeFullWrap}>
              <button
                type="button"
                className={styles.closeFullBtn}
                onClick={() => {
                  setIsFullOpen(false);
                  const el = document.getElementById('vision-mission');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                Collapse Story ▴
              </button>
            </div>
          </div>
        )}


      </div>
    </section>
  );
}
